<template>
  <v-container fluid>
    <base-material-card color="primary" icon="mdi-chart-line" inline class="ml-0 px-5 py-4">
      <template v-slot:after-heading>
        <div class="display-2 font-weight-light">Aging - Painel Gerencial</div>
      </template>

      <div v-if="loading" class="py-12 text-center">
        <v-progress-circular indeterminate color="primary" size="64" width="3" />
        <div class="body-1 grey--text mt-4">Carregando painel gerencial...</div>
      </div>

      <v-alert v-else-if="!execucao" dense outlined type="error" class="my-4">
        Não foi possível carregar os dados do painel gerencial.
      </v-alert>

      <template v-else>
        <v-row align="center" class="my-2">
          <v-col cols="12" md="8">
            <div class="display-1 font-weight-light">
              <span class="grey--text text--darken-1">#{{ execucao.id }}</span>
              <span class="grey--text mx-3 text--lighten-1">|</span>
              {{ execucao.titulo }}
              <span class="grey--text mx-3 text--lighten-1">|</span>
              {{ periodoFormatado }}
            </div>
          </v-col>
          <v-col cols="12" md="4" class="text-md-right">
            <v-btn color="primary" depressed @click="$emit('download-resultado')">
              <v-icon left>mdi-download</v-icon>
              Baixar resultado
            </v-btn>
          </v-col>
        </v-row>

        <v-row class="mb-6">
          <v-col cols="12">
            <div class="align-center body-1 d-flex flex-wrap grey--text text--darken-2">
              <general-status :status="execucao.status" class="mr-4" />
              <span class="d-none d-sm-inline grey--text mx-3 text--lighten-1">|</span>
              <span>
                Executado:
                <strong class="grey--text text--darken-4">{{ execucao.dataExecucao | parseToDateTimeBR }}</strong>
              </span>
              <span class="grey--text mx-3 text--lighten-1">|</span>
              <span>
                Autor:
                <strong class="grey--text text--darken-4">{{ execucao.autor }}</strong>
              </span>
              <span class="grey--text mx-3 text--lighten-1">|</span>
              <span>
                Base resumo:
                <strong class="grey--text text--darken-4">{{ execucao.tipoBaseResumo || execucao.tipoBase }}</strong>
              </span>
            </div>
          </v-col>
        </v-row>

        <v-row class="mb-4" justify="end">
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-select
              v-model="tucFiltro"
              :items="tucsOpcoes"
              item-text="label"
              item-value="value"
              label="TUC"
              hide-details
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6" md="4" lg="3">
            <v-select
              v-model="mesesFiltro"
              :items="mesesOpcoes"
              item-text="label"
              item-value="value"
              label="Ano/Mês"
              hide-details
              dense
              outlined
              multiple
              clearable
              :menu-props="{ maxHeight: 320 }"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip v-if="index < 2" small class="ma-0 mr-1">{{ item.label }}</v-chip>
                <span v-if="index === 2" class="grey--text text-caption">(+{{ mesesFiltro.length - 2 }})</span>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <v-alert v-if="!temDados" dense outlined type="info" color="blue" class="my-4">
          {{ mensagemSemDados }}
        </v-alert>

        <v-row v-if="!temDados">
          <v-col cols="12">
            <v-card outlined class="ma-0 pa-8 text-center" color="#F7FAFA">
              <v-icon size="72" color="grey lighten-1">mdi-chart-line</v-icon>
              <div class="font-weight-medium grey--text headline mt-4 text--darken-2">{{ mensagemSemDados }}</div>
              <div class="body-2 grey--text mt-2">
                Ajuste os filtros de TUC ou Ano/Mês, ou reexecute o Aging para gerar os dados do resumo.
              </div>
            </v-card>
          </v-col>
        </v-row>

        <template v-if="temDados">
          <v-row align="stretch">
            <v-col cols="12" md="4" class="d-flex">
              <v-card outlined class="flex-grow-1 ma-0 pa-5">
                <div class="caption font-weight-medium grey--text text--darken-1 text-uppercase">Total de TUCs</div>
                <div class="font-weight-medium headline mt-2 primary--text">
                  {{ dadosPainel.totalTucs | parseNumberToIntegerBR }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" md="4" class="d-flex">
              <v-card outlined class="flex-grow-1 ma-0 pa-5">
                <div class="caption font-weight-medium grey--text text--darken-1 text-uppercase">
                  Qtd totalmente depreciada (partida)
                </div>
                <div class="font-weight-medium headline mt-2 primary--text">
                  {{ dadosPainel.qtdPartida | parseNumberToDecimalBR }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" md="4" class="d-flex">
              <v-card outlined class="flex-grow-1 ma-0 pa-5">
                <div class="caption font-weight-medium grey--text text--darken-1 text-uppercase">
                  Valor totalmente depreciado (partida)
                </div>
                <div class="font-weight-medium headline mt-2 primary--text">
                  {{ dadosPainel.valorPartida | parseNumberToDecimalBR }}
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mt-6">
            <v-col cols="12">
              <v-card outlined class="ma-0 pa-4">
                <div class="font-weight-medium mb-4 subtitle-1">Evolução da depreciação (aba Resumo)</div>
                <highcharts v-if="temDadosGrafico" :options="graficoLinhasOptions" />
                <div v-else class="grey--text py-8 text-center">
                  <v-icon size="48" color="grey lighten-1">mdi-chart-line</v-icon>
                  <div class="body-1 mt-3">Não há dados mensais para exibir no gráfico.</div>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mt-6">
            <v-col cols="12">
              <v-card outlined class="ma-0 pa-4">
                <div class="align-center d-flex flex-wrap mb-4">
                  <div class="font-weight-medium subtitle-1">Detalhamento por TUC</div>
                  <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Procurar TUC"
                    hide-details
                    dense
                    class="ml-auto"
                    style="max-width: 280px"
                  />
                </div>
                <v-data-table
                  :headers="headersDetalhamento"
                  :items="tabelaDetalhamento"
                  :search="search"
                  :items-per-page="10"
                  :footer-props="{ itemsPerPageText: 'Itens por Página' }"
                  :header-props="{ sortByText: 'Ordenar Por' }"
                  no-data-text="Nenhuma TUC para exibir"
                  no-results-text="Nenhuma TUC encontrada na busca"
                  dense
                  mobile-breakpoint="0"
                >
                  <template v-slot:[`item.tuc`]="{ item }">
                    <span class="font-weight-medium">{{ item.tuc }}</span>
                  </template>
                  <template v-slot:[`item.qtdPartida`]="{ item }">
                    {{ item.qtdPartida | parseNumberToDecimalBR }}
                  </template>
                  <template v-slot:[`item.valorPartida`]="{ item }">
                    {{ item.valorPartida | parseNumberToDecimalBR }}
                  </template>
                  <template v-slot:[`item.qtdUltimoMes`]="{ item }">
                    {{ item.qtdUltimoMes | parseNumberToDecimalBR }}
                  </template>
                  <template v-slot:[`item.valorUltimoMes`]="{ item }">
                    {{ item.valorUltimoMes | parseNumberToDecimalBR }}
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </template>
    </base-material-card>
  </v-container>
</template>

<script>
  import Highcharts from 'highcharts';
  import HighchartsNoData from 'highcharts/modules/no-data-to-display';

  HighchartsNoData(Highcharts);

  export default {
    name: 'DashboardGerencialAging',

    components: {
      GeneralStatus: () => import('@/components/general/GeneralStatus.vue')
    },

    props: {
      execucao: {
        type: Object,
        default: null
      },
      loading: {
        type: Boolean,
        default: false
      }
    },

    emits: ['download-resultado'],

    data() {
      return {
        search: '',
        tucFiltro: null,
        mesesFiltro: [],
        headersDetalhamento: [
          { text: 'TUC', value: 'tuc', width: '100px' },
          { text: 'Qtd Partida', value: 'qtdPartida', align: 'end' },
          { text: 'Valor Partida', value: 'valorPartida', align: 'end' },
          { text: 'Qtd Último Mês', value: 'qtdUltimoMes', align: 'end' },
          { text: 'Valor Último Mês', value: 'valorUltimoMes', align: 'end' }
        ]
      };
    },

    computed: {
      periodoFormatado() {
        const dataBase = this.formatDateLabel(this.execucao.dataBase);
        const dataProjetada = this.formatDateLabel(this.execucao.dataProjetada);
        return `${dataBase} → ${dataProjetada}`;
      },

      resumoPorTuc() {
        return this.execucao?.resumoPorTuc || [];
      },

      mesesDisponiveis() {
        return this.execucao?.meses || [];
      },

      tucsOpcoes() {
        const opcoes = [{ value: null, label: 'Todas' }];
        this.resumoPorTuc.forEach((item) => {
          opcoes.push({ value: item.tuc, label: String(item.tuc) });
        });
        return opcoes;
      },

      mesesOpcoes() {
        return this.mesesDisponiveis.map((mes) => ({
          value: mes,
          label: mes
        }));
      },

      mesesSelecionados() {
        if (!this.mesesFiltro || this.mesesFiltro.length === 0) {
          return this.mesesDisponiveis;
        }

        return this.mesesDisponiveis.filter((mes) => this.mesesFiltro.includes(mes));
      },

      dadosPainel() {
        if (!this.execucao) {
          return this.dadosPainelVazio();
        }

        const linhas = this.tucFiltro
          ? this.resumoPorTuc.filter((item) => item.tuc === this.tucFiltro)
          : this.resumoPorTuc;

        let qtdPartida = 0;
        let valorPartida = 0;
        const serieMensal = {};

        this.mesesSelecionados.forEach((mes) => {
          serieMensal[mes] = { qtd: 0, valor: 0 };
        });

        linhas.forEach((linha) => {
          qtdPartida += Number(linha.qtdPartida || 0);
          valorPartida += Number(linha.valorPartida || 0);

          this.mesesSelecionados.forEach((mes) => {
            const dadosMes = linha.meses?.[mes] || { qtd: 0, valor: 0 };
            serieMensal[mes].qtd += Number(dadosMes.qtd || 0);
            serieMensal[mes].valor += Number(dadosMes.valor || 0);
          });
        });

        return {
          totalTucs: linhas.length,
          qtdPartida,
          valorPartida,
          serieMensal,
          resumoPorTuc: linhas
        };
      },

      temDados() {
        return this.resumoPorTuc.length > 0 && this.dadosPainel.totalTucs > 0;
      },

      temDadosGrafico() {
        return this.mesesSelecionados.length > 0;
      },

      mensagemSemDados() {
        if (this.tucFiltro) {
          return `Nenhum dado encontrado para a TUC ${this.tucFiltro}.`;
        }
        if (this.mesesFiltro?.length) {
          return 'Nenhum dado encontrado para o Ano/Mês selecionado.';
        }
        return 'Esta execução não possui dados da aba Resumo para análise.';
      },

      tabelaDetalhamento() {
        const ultimoMes = this.mesesSelecionados[this.mesesSelecionados.length - 1] || null;

        return this.dadosPainel.resumoPorTuc.map((item) => {
          const dadosUltimoMes = ultimoMes ? item.meses?.[ultimoMes] || { qtd: 0, valor: 0 } : { qtd: 0, valor: 0 };

          return {
            tuc: item.tuc,
            qtdPartida: item.qtdPartida,
            valorPartida: item.valorPartida,
            qtdUltimoMes: dadosUltimoMes.qtd,
            valorUltimoMes: dadosUltimoMes.valor
          };
        });
      },

      graficoLinhasOptions() {
        const categorias = ['Partida', ...this.mesesSelecionados];
        const serieValor = [this.dadosPainel.valorPartida];
        const serieQtd = [this.dadosPainel.qtdPartida];

        this.mesesSelecionados.forEach((mes) => {
          const ponto = this.dadosPainel.serieMensal[mes] || { qtd: 0, valor: 0 };
          serieValor.push(ponto.valor);
          serieQtd.push(ponto.qtd);
        });

        return {
          chart: {
            type: 'line',
            height: 420
          },
          title: { text: null },
          credits: { enabled: false },
          legend: { enabled: true },
          xAxis: {
            categories: categorias,
            title: { text: null },
            labels: {
              style: { fontSize: '12px' }
            }
          },
          yAxis: [
            {
              title: { text: 'Valor totalmente depreciado' },
              labels: {
                formatter() {
                  return Number(this.value).toLocaleString('pt-BR');
                }
              }
            },
            {
              title: { text: 'Quantidade' },
              opposite: true,
              labels: {
                formatter() {
                  return Number(this.value).toLocaleString('pt-BR');
                }
              }
            }
          ],
          tooltip: {
            shared: true,
            formatter() {
              return this.points
                .map((point) => {
                  const valor = Number(point.y).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  });
                  return `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${valor}</b>`;
                })
                .join('<br/>');
            }
          },
          plotOptions: {
            line: {
              marker: { enabled: true, radius: 4 },
              lineWidth: 2
            }
          },
          series: [
            {
              name: 'Valor totalmente depreciado',
              data: serieValor,
              color: '#1976D2',
              yAxis: 0
            },
            {
              name: 'Qtd totalmente depreciada',
              data: serieQtd,
              color: '#00897B',
              yAxis: 1
            }
          ],
          lang: {
            noData: 'Não há dados mensais para exibir no gráfico.'
          },
          noData: {
            style: {
              fontWeight: 'bold',
              fontSize: '15px',
              color: '#303030'
            }
          }
        };
      }
    },

    methods: {
      formatDateLabel(value) {
        if (!value) return '-';
        if (typeof value === 'string' && value.includes('/')) {
          return value.split(' ')[0];
        }
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
          return String(value).split('T')[0] || '-';
        }
        return date.toLocaleDateString('pt-BR');
      },

      dadosPainelVazio() {
        return {
          totalTucs: 0,
          qtdPartida: 0,
          valorPartida: 0,
          serieMensal: {},
          resumoPorTuc: []
        };
      }
    }
  };
</script>
